import { t } from 'i18next';
import { isAxiosError } from 'axios';
import {
  CAMPAIGN_STATUSES,
  CampaignStatus,
} from '@/services/outbound-calls-service/outbound-calls.constants';
import { formatNumber } from '@/i18n/format';
import { getApiErrorMessage } from '@/utils/api-error/api-error';
import { languageName } from '@/utils/language-name';

// The API words these in English, one sentence per state. Each rule names one known sentence;
// anything else, a Twilio error for one, is shown as the API sent it.
type Rule = [pattern: RegExp, render: (match: RegExpExecArray) => string];

const translateBy =
  (rules: Rule[]) =>
  (message: string): string => {
    for (const [pattern, render] of rules) {
      const match = pattern.exec(message);
      if (match) return render(match);
    }
    return message;
  };

// Python's `%a` in the server's C locale.
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY = `(${DAY_NAMES.join('|')})`;

const weekday = (name: string) =>
  t(`outbound:weekdays.${DAY_NAMES.indexOf(name) + 1}` as 'outbound:weekdays.1');

const statusLabel = (status: string) =>
  (CAMPAIGN_STATUSES as readonly string[]).includes(status)
    ? t(`outbound:campaignStatus.${status as CampaignStatus}`)
    : status;

export const campaignActivityLabel = translateBy([
  [/^Not started\.$/, () => t('outbound:activity.notStarted')],
  [/^Paused\. Resume it to continue dialling\.$/, () => t('outbound:activity.paused')],
  [/^Finished\.$/, () => t('outbound:activity.finished')],
  [/^Dialling the next entry within a minute\.$/, () => t('outbound:activity.dialling')],
  [
    /^Nothing left to call\. The campaign finishes within a minute\.$/,
    () => t('outbound:activity.finishing'),
  ],
  [/^Waiting: no salesperson is available\.$/, () => t('outbound:activity.noSalesperson')],
  [
    new RegExp(`^Waiting: outside the calling window, it opens ${DAY} (\\d{2}:\\d{2})\\.$`),
    ([, day, time]) => t('outbound:activity.windowOpens', { day: weekday(day), time }),
  ],
  [/^Waiting: outside the calling window\.$/, () => t('outbound:activity.outsideWindow')],
  [
    /^Waiting: a call to (\S+) \((.+)\) is on the air\.$/,
    ([, phone, campaign]) => t('outbound:activity.otherCampaignCall', { phone, campaign }),
  ],
  [
    /^Waiting: a call to (\S+) is on the air\.$/,
    ([, phone]) => t('outbound:activity.onTheAir', { phone }),
  ],
  [
    /^Waiting: pause between calls until ([\d:]+)\.$/,
    ([, time]) => t('outbound:activity.pause', { time }),
  ],
  [
    new RegExp(`^Waiting: the next retry is due at ${DAY} (\\d{2}:\\d{2})\\.$`),
    ([, day, time]) => t('outbound:activity.retryDue', { day: weekday(day), time }),
  ],
  [
    new RegExp(`^Waiting: the next callback is due at ${DAY} (\\d{2}:\\d{2})\\.$`),
    ([, day, time]) => t('outbound:activity.callbackDue', { day: weekday(day), time }),
  ],
]);

/** A refused action (`code: campaign_refused`), and the reason a campaign paused itself. */
export const campaignRefusalLabel = translateBy([
  [/^Only a draft campaign can be started\.$/, () => t('outbound:refusal.onlyDraftStarts')],
  [
    /^The campaign has nothing to call\. Import a list first\.$/,
    () => t('outbound:refusal.nothingToCall'),
  ],
  [/^The scenario is inactive\.$/, () => t('outbound:refusal.scenarioInactive')],
  [/^Company outgoing calls phone must be set$/, () => t('outbound:refusal.noOutgoingPhone')],
  [
    /^Language not supported: (.+)$/,
    ([, language]) =>
      t('outbound:refusal.languageNotSupported', { language: languageName(language) }),
  ],
  [
    /^Another campaign of this company is already running\.$/,
    () => t('outbound:refusal.anotherRunning'),
  ],
  [
    /^The campaign is finished\. Create a new one for this list\.$/,
    () => t('outbound:refusal.finishedImport'),
  ],
  [
    /^The campaign is (\w+), not (\w+)\.$/,
    ([, status, expected]) =>
      t('outbound:refusal.wrongStatus', {
        status: statusLabel(status),
        expected: statusLabel(expected),
      }),
  ],
  [
    /^(\d+) calls in a row failed, last: ([\s\S]+)$/,
    ([, failures, error]) => t('outbound:refusal.failuresInARow', { failures, error }),
  ],
  [
    new RegExp(
      `^A callback is due ${DAY} ([\\d.]+ [\\d:]+), but another campaign of this company is running\\.`
    ),
    ([, day, when]) => t('outbound:refusal.callbackBlocked', { when: `${weekday(day)} ${when}` }),
  ],
]);

const callbackRefusalLabel = translateBy([
  [/^the number is on the do-not-call list$/, () => t('outbound:entryError.onDoNotCallList')],
  [/^the number reached someone else$/, () => t('outbound:entryError.reachedSomeoneElse')],
  [
    /^the campaign allows (\d+) callbacks per number$/,
    ([, max]) => t('outbound:entryError.callbackLimit', { max }),
  ],
  [/^the time has passed$/, () => t('outbound:entryError.timePassed')],
  [
    /^it is more than (\d+) days ahead$/,
    ([, days]) => t('outbound:entryError.tooFarAhead', { days }),
  ],
  [/^the campaign has no calling day$/, () => t('outbound:entryError.noCallingDay')],
]);

/** An entry's `last_error`. A failed dial keeps the provider's own words. */
export const entryErrorLabel = translateBy([
  [/^do not call$/, () => t('outbound:entryError.doNotCall')],
  [/^wrong number$/, () => t('outbound:entryError.wrongNumber')],
  [
    new RegExp(`^callback ${DAY} ([\\d.]+ [\\d:]+) not booked: (.+)$`),
    ([, day, when, reason]) =>
      t('outbound:entryError.callbackNotBooked', {
        when: `${weekday(day)} ${when}`,
        reason: callbackRefusalLabel(reason),
      }),
  ],
]);

/** The import's `errors.file`. */
export const importFileErrorLabel = translateBy([
  [/^No file was submitted\.$/, () => t('outbound:importError.noFile')],
  [/^The (submitted )?file is empty\.$/, () => t('outbound:importError.empty')],
  [
    /^The file is larger than ([\d.]+) MB\.$/,
    ([, size]) => t('outbound:importError.tooLarge', { size }),
  ],
  [/^The file is not UTF-8\./, () => t('outbound:importError.notUtf8')],
  [
    /^Missing column: (.+)\.$/,
    ([, column]) =>
      t('outbound:importError.missingColumn', { column: column.replace(' or ', ' lub ') }),
  ],
  [
    /^The file has more than (\d+) rows\./,
    ([, max]) => t('outbound:importError.tooManyRows', { max: formatNumber(Number(max)) }),
  ],
]);

const PHONE_RULES: Rule[] = [
  [/^no phone number$/, () => t('outbound:phoneProblem.missing')],
  [/^not a phone number: (.*)$/, ([, raw]) => t('outbound:phoneProblem.notAPhone', { raw })],
  [
    /^no country code and not a 9-digit Polish number: (.*)$/,
    ([, raw]) => t('outbound:phoneProblem.noCountryCode', { raw }),
  ],
  [
    /^a Polish number has 9 digits after \+48: (.*)$/,
    ([, raw]) => t('outbound:phoneProblem.polishLength', { raw }),
  ],
  [
    /^not a valid international number: (.*)$/,
    ([, raw]) => t('outbound:phoneProblem.invalid', { raw }),
  ],
  [
    /^only \+48 and \+1 numbers can be dialled: (.*)$/,
    ([, raw]) => t('outbound:phoneProblem.onlyPlAndUs', { raw }),
  ],
  [
    /^several numbers added together in the export: (.*)$/,
    ([, raw]) => t('outbound:phoneProblem.summed', { raw }),
  ],
];

const phoneProblemLabel = translateBy(PHONE_RULES);

/** A row the import left out. Several reasons quote the value from the file. */
export const skipReasonLabel = translateBy([
  [/^missing company name$/, () => t('outbound:skipReason.missingName')],
  [/^invalid NIP: (.*)$/, ([, nip]) => t('outbound:skipReason.invalidNip', { nip })],
  [/^contact without a phone number$/, () => t('outbound:skipReason.contactWithoutPhone')],
  [
    /^contact phone: (.+)$/,
    ([, problem]) => t('outbound:skipReason.contactPhone', { problem: phoneProblemLabel(problem) }),
  ],
  [
    /^company phone ignored: (.+)$/,
    ([, problem]) => t('outbound:skipReason.companyPhone', { problem: phoneProblemLabel(problem) }),
  ],
  [/^(.+): no phone number$/, ([, name]) => t('outbound:skipReason.noPhone', { name })],
  [/^marked in the list: (.+)$/, ([, note]) => t('outbound:skipReason.statusNote', { note })],
  [
    /^letters lost in the export: (.+)$/,
    ([, name]) => t('outbound:skipReason.lostLetters', { name }),
  ],
]);

/** A 422 message on one field, from the serializer or the model. */
export const fieldErrorLabel = translateBy([
  [/^This field is required\.$/, () => t('outbound:fieldError.required')],
  [/^has already been taken$/, () => t('outbound:fieldError.taken')],
  [/^is not a scenario of this company$/, () => t('outbound:fieldError.notCompanyScenario')],
  [/^is inactive$/, () => t('outbound:fieldError.scenarioInactive')],
  [/^the company has no voice for this language$/, () => t('outbound:fieldError.noVoice')],
  [/^must be later than the window start$/, () => t('outbound:fieldError.windowOrder')],
  [/^pick at least one day$/, () => t('outbound:fieldError.pickDay')],
  [/^is already a salesperson$/, () => t('outbound:fieldError.alreadySalesperson')],
  [/^is not a user of this company$/, () => t('outbound:fieldError.notCompanyUser')],
  [/^set both hours or neither$/, () => t('outbound:fieldError.bothHours')],
  [/^must be later than the start$/, () => t('outbound:fieldError.hoursOrder')],
  [/^Enter the number as (.+)$/, ([, phone]) => t('outbound:fieldError.enterAs', { phone })],
  [
    /^Ensure this value is less than or equal to (\d+)\.$/,
    ([, max]) => t('outbound:fieldError.max', { max }),
  ],
  [
    /^Ensure this value is greater than or equal to (\d+)\.$/,
    ([, min]) => t('outbound:fieldError.min', { min }),
  ],
  [/^A valid integer is required\.$/, () => t('outbound:fieldError.integer')],
  [
    /^Ensure this field has no more than (\d+) characters\.$/,
    ([, max]) => t('outbound:fieldError.maxLength', { max }),
  ],
  ...PHONE_RULES,
]);

const notFoundLabel = translateBy([
  [/^Company not found$/, () => t('outbound:errors.companyNotFound')],
  [/^Campaign not found$/, () => t('outbound:errors.campaignNotFound')],
  [/^Lead( contact)? not found$/, () => t('outbound:errors.leadNotFound')],
  [/^Outbound call not found$/, () => t('outbound:errors.callNotFound')],
  [/^Scenario not found$/, () => t('outbound:errors.scenarioNotFound')],
  [/^Salesperson not found$/, () => t('outbound:errors.salespersonNotFound')],
]);

export const isNotPermitted = (error: unknown) =>
  isAxiosError(error) &&
  (error.response?.data as { code?: unknown } | undefined)?.code === 'not_permitted';

export const isNotFound = (error: unknown) => isAxiosError(error) && error.response?.status === 404;

/**
 * What to tell the user about a failed request: the role refusal, a record that is not
 * there, or a refused action, each in Polish. Field errors belong on the form instead.
 */
export const outboundErrorMessage = (error: unknown, fallback: string) => {
  if (isNotPermitted(error)) return t('outbound:errors.notPermitted');
  const message = getApiErrorMessage(error, '');
  if (!message) return fallback;
  return isNotFound(error) ? notFoundLabel(message) : campaignRefusalLabel(message);
};
